import { useState, useEffect } from "react";
import axios from "axios";
import QMSLayout from "../components/QMSLayout";
import { API, useAuth } from "../App";
import { useTranslation } from "react-i18next";
import {
  Mail, Send, Inbox, CheckCircle, Circle, Clock,
  ChevronRight, Plus, Loader2, X, ArrowLeft
} from "lucide-react";

const MessagesPage = () => {
  const { t } = useTranslation('platform');
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState("inbox");
  const [selected, setSelected] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ recipient_id: "", subject: "", body: "", priority: "normal" });

  useEffect(() => { fetchMessages(); fetchUsers(); }, [folder]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/messages?folder=${folder}`, { withCredentials: true });
      setMessages(res.data.messages || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(res.data || []);
    } catch (e) { /* non-admin may not access */ }
  };

  const handleSend = async () => {
    if (!form.recipient_id || !form.subject || !form.body) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, form, { withCredentials: true });
      setShowCompose(false);
      setForm({ recipient_id: "", subject: "", body: "", priority: "normal" });
      fetchMessages();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  const markRead = async (msg) => {
    if (!msg.read && folder === "inbox") {
      await axios.put(`${API}/messages/${msg.message_id}/read`, {}, { withCredentials: true });
    }
    setSelected(msg);
  };

  const priorityColors = { urgent: "#F87171", high: "#FBBF24", normal: "#8B95A5", low: "#64748B" };

  return (
    <QMSLayout title={t('platform:messages.title')}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Syne'] text-2xl font-bold text-white" data-testid="messages-title">{t('messages.title')}</h2>
          <p className="text-[#8B95A5] text-sm mt-1">
            {unreadCount > 0 ? t('messages.unreadCount', { count: unreadCount }) : t('messages.allCaughtUp')}
          </p>
        </div>
        <button onClick={() => setShowCompose(true)} className="btn-primary flex items-center gap-2" data-testid="compose-btn">
          <Plus size={16} /> {t('messages.compose')}
        </button>
      </div>

      {showCompose && (
        <div className="qms-card mb-6" data-testid="compose-form">
          <div className="qms-card-body space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2"><Send size={16} className="text-[#36A9E1]" /> {t('messages.newMessage')}</h3>
              <button onClick={() => setShowCompose(false)} className="text-[#8B95A5] hover:text-white"><X size={16} /></button>
            </div>
            <select value={form.recipient_id} onChange={e => setForm(p => ({ ...p, recipient_id: e.target.value }))}
              className="qms-input w-full" data-testid="msg-recipient">
              <option value="">{t('messages.selectRecipient')}</option>
              {users.filter(u => u.user_id !== user?.user_id).map(u => (
                <option key={u.user_id} value={u.user_id}>{u.name} ({u.role})</option>
              ))}
            </select>
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
              className="qms-input w-full" data-testid="msg-priority">
              <option value="normal">{t('messages.normalPriority')}</option>
              <option value="high">{t('messages.highPriority')}</option>
              <option value="urgent">{t('messages.urgent')}</option>
            </select>
            <input type="text" placeholder={t('messages.subject')} value={form.subject}
              onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
              className="qms-input w-full" data-testid="msg-subject" />
            <textarea placeholder={t('messages.writeMessage')} value={form.body}
              onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              className="qms-input w-full h-32" data-testid="msg-body" />
            <button onClick={handleSend} disabled={sending || !form.recipient_id || !form.subject}
              className="btn-primary flex items-center gap-2" data-testid="msg-send-btn">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} {t('messages.send')}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Folder Tabs + List */}
        <div className="lg:col-span-1">
          <div className="flex gap-2 mb-3">
            {[
              { id: "inbox", label: t('messages.inbox'), icon: Inbox },
              { id: "sent", label: t('messages.sent'), icon: Send },
            ].map(f => (
              <button key={f.id} onClick={() => { setFolder(f.id); setSelected(null); }}
                className={`flex items-center gap-2 px-3 py-2 rounded text-xs font-medium transition-all ${
                  folder === f.id ? "bg-[#36A9E1] text-white" : "bg-[#1F222D] text-[#8B95A5] hover:bg-[#2D313D]"
                }`} data-testid={`folder-${f.id}`}>
                <f.icon size={14} /> {f.label}
                {f.id === "inbox" && unreadCount > 0 && (
                  <span className="bg-[#F87171] text-white text-[10px] px-1.5 rounded-full">{unreadCount}</span>
                )}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {loading ? <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#36A9E1]" size={20} /></div>
             : messages.length === 0 ? (
              <div className="text-center text-[#8B95A5] text-sm py-8">No messages</div>
            ) : messages.map(msg => (
              <div key={msg.message_id} onClick={() => markRead(msg)}
                className={`qms-card cursor-pointer hover:border-[#36A9E1]/30 transition-all ${selected?.message_id === msg.message_id ? "border-[#36A9E1]/50" : ""} ${!msg.read && folder === "inbox" ? "border-l-2 border-l-[#36A9E1]" : ""}`}
                data-testid={`msg-item-${msg.message_id}`}>
                <div className="qms-card-body py-2.5">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${!msg.read ? "text-white" : "text-[#8B95A5]"}`}>
                      {folder === "inbox" ? msg.sender_name : msg.recipient_id}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: priorityColors[msg.priority] }} />
                      <span className="text-[#8B95A5] text-[10px]">{new Date(msg.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <p className={`text-xs mt-0.5 truncate ${!msg.read ? "text-[#CBD5E1]" : "text-[#64748B]"}`}>{msg.subject}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Message Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="qms-card" data-testid="msg-detail">
              <div className="qms-card-body">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">{selected.subject}</h3>
                  <span className="text-[#8B95A5] text-xs">{new Date(selected.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-[#8B95A5] text-xs">From: <strong className="text-white">{selected.sender_name}</strong></span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{
                    color: priorityColors[selected.priority],
                    backgroundColor: `${priorityColors[selected.priority]}20`
                  }}>{selected.priority}</span>
                </div>
                <div className="bg-[#111318] rounded p-4 text-[#CBD5E1] text-sm whitespace-pre-line">{selected.body}</div>
              </div>
            </div>
          ) : (
            <div className="qms-card"><div className="qms-card-body text-center text-[#8B95A5] py-16">
              <Mail size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Select a message to read</p>
            </div></div>
          )}
        </div>
      </div>
    </QMSLayout>
  );
};

export default MessagesPage;
